import React from 'react';
import { motion } from 'motion/react';
import { Shield, Bell, User, Settings, ToggleLeft, ToggleRight } from 'lucide-react';

const PreferencesCard: React.FC = () => {
  const preferences = [
    {
      icon: Bell,
      label: 'Email Notifications',
      description: 'Receive updates about your energy systems',
      enabled: true,
    },
    {
      icon: Shield,
      label: 'Two-Factor Authentication',
      description: 'Enhanced security for your account',
      enabled: true,
    },
    {
      icon: User,
      label: 'Public Profile',
      description: 'Make your profile visible to other users',
      enabled: false,
    },
    {
      icon: Settings,
      label: 'Auto-Save Data',
      description: 'Automatically save system performance data',
      enabled: true,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.6 }}
      className="rounded-3xl border border-gray-100/50 bg-white/80 p-6 shadow-xl backdrop-blur-sm transition-all duration-300 hover:shadow-2xl"
    >
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 shadow-lg">
          <Shield className="h-6 w-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Preferences</h2>
          <p className="text-sm text-gray-600">Customize your experience</p>
        </div>
      </div>

      {/* Preferences List */}
      <div className="space-y-4">
        {preferences.map((pref, index) => (
          <motion.div
            key={pref.label}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 + index * 0.1, duration: 0.3 }}
            className="flex items-center justify-between rounded-2xl border border-gray-100/50 bg-gray-50/30 p-4 transition-all duration-200 hover:bg-gray-50/60"
          >
            <div className="flex items-center gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white shadow-sm">
                <pref.icon className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{pref.label}</p>
                <p className="text-sm text-gray-600">{pref.description}</p>
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`flex items-center rounded-full p-1 transition-all duration-200 ${
                pref.enabled ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 hover:bg-gray-400'
              }`}
            >
              {pref.enabled ? (
                <ToggleRight className="h-6 w-6 text-white" />
              ) : (
                <ToggleLeft className="h-6 w-6 text-gray-600" />
              )}
            </motion.button>
          </motion.div>
        ))}
      </div>

      {/* Additional Settings */}
      <div className="mt-6 rounded-2xl bg-gradient-to-r from-blue-50/50 to-purple-50/50 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-semibold text-gray-900">Account Status</h4>
            <p className="text-sm text-gray-600">Premium account with full access</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 animate-pulse rounded-full bg-emerald-500"></div>
            <span className="text-sm font-medium text-emerald-700">Active</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PreferencesCard;
